import Box from '@mui/material/Box';
import BarChartIcon from '@mui/icons-material/BarChart';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import WarningIcon from '@mui/icons-material/Warning';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { useNavigate } from 'react-router';
import { mindtimeWazuhMitreApi } from 'src/api/mindtime/wazuhMitreApi';
import { Table } from 'src/common';
import { getSecurityMitreDetailHref } from 'src/mindtime/config/securityNav';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import type {
  WazuhMitreAgentSummaryData,
  WazuhMitreQuery,
  WazuhMitreTechniqueItem,
} from 'src/types/mindtime/wazuhMitre';
import type { Column, TableData } from 'src/types/table';
import logger from 'src/utils/logger';
import {
  getMitreTechniqueId,
  getMitreTechniqueListColumns,
  MITRE_SEVERITY_FILTER_OPTIONS,
} from '../../mitre-attck/mitreDetailModel';
import { getAgentMitrePreview } from '../../preview/mitre';
import {
  EDR_COLORS,
  EdrDataCard,
  EdrDataToolbar,
  EdrKpiCard,
  EdrStatusPill,
  type EdrToolbarFilter,
  formatEdrNumber,
}  from '../../shared/edrPrimitives';

const PAGE_SIZE = 25;

type Filters = {
  tactic?: string;
  technique?: string;
  severityBand?: string;
  mitreId?: string;
};


const buildListQuery = (
  systemWazuhAgentId: string,
  page: number,
  pageSize: number,
  search: string,
  filters: Filters
): WazuhMitreQuery => ({
  systemWazuhAgentId,
  page,
  pageSize,
  ...(search.trim() ? { search: search.trim() } : {}),
  ...(filters.tactic ? { tactic: filters.tactic } : {}),
  ...(filters.technique ? { technique: filters.technique } : {}),
  ...(filters.severityBand ? { severityBand: filters.severityBand } : {}),
  ...(filters.mitreId ? { mitreId: filters.mitreId } : {}),
});

type EndpointMitreTabProps = {
  systemWazuhAgentId: string;
};

const EndpointMitreTab: FC<EndpointMitreTabProps> = ({ systemWazuhAgentId }) => {
  const navigate = useNavigate();
  const { wait, isPreviewMode } = useEdrDataGate('endpoints');
  const { canWidget } = useSubscriptionModulePermissions(EDR_SOLUTION_ID, 'mitre-attck');
  const canSummary = canWidget('summary');
  const canList = canWidget('techniquesList');
  const agentId = systemWazuhAgentId.trim();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Filters>({});
  const [tacticFacets, setTacticFacets] = useState<string[]>([]);
  const [techniqueFacets, setTechniqueFacets] = useState<string[]>([]);
  const [mitreIdFacets, setMitreIdFacets] = useState<string[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [summary, setSummary] = useState<WazuhMitreAgentSummaryData | null>(null);
  const [items, setItems] = useState<WazuhMitreTechniqueItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
        setPage(0);
      }, 300),
    []
  );

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const openTechniqueDetail = useCallback(
    (item: WazuhMitreTechniqueItem) => {
      const id = getMitreTechniqueId(item);
      if (!id) return;
      navigate(getSecurityMitreDetailHref(id), { state: { record: item } });
    },
    [navigate]
  );

  useEffect(() => {
    if (!agentId) {
      setSummary(null);
      return undefined;
    }
    if (wait) return undefined;
    if (isPreviewMode) {
      setSummary(canSummary ? getAgentMitrePreview(agentId).summary : null);
      return undefined;
    }

    if (!canSummary) {
      setSummary(null);
      return undefined;
    }

    let cancelled = false;
    mindtimeWazuhMitreApi
      .getAgentSummary(agentId)
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'EndpointMitreTab.summary');
          setSummary(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, wait, isPreviewMode, canSummary]);

  useEffect(() => {
    if (!agentId) {
      setItems([]);
      setTotalRecords(0);
      setListLoading(false);
      return undefined;
    }
    if (wait) {
      setListLoading(true);
      return undefined;
    }
    if (isPreviewMode) {
      if (!canList) {
        setItems([]);
        setTotalRecords(0);
        setListLoading(false);
        return undefined;
      }
      const preview = getAgentMitrePreview(agentId);
      setItems(preview.items);
      setTotalRecords(preview.items.length);
      setListLoading(false);
      return undefined;
    }

    if (!canList) {
      setItems([]);
      setTotalRecords(0);
      setListLoading(false);
      return undefined;
    }

    let cancelled = false;
    setListLoading(true);
    mindtimeWazuhMitreApi
      .getTechniques(buildListQuery(agentId, page, rowsPerPage, search, filters))
      .then((data) => {
        if (cancelled) return;
        setItems(data?.items ?? []);
        setTotalRecords(data?.totalRecords ?? 0);
        setListLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        logger(error, 'error', 'EndpointMitreTab.list');
        setItems([]);
        setTotalRecords(0);
        setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    agentId,
    page,
    rowsPerPage,
    search,
    filters.tactic,
    filters.technique,
    filters.severityBand,
    filters.mitreId,
    wait,
    isPreviewMode,
    canList,
  ]);

  useEffect(() => {
    setTacticFacets([]);
    setTechniqueFacets([]);
    setMitreIdFacets([]);
  }, [agentId]);

  useEffect(() => {
    if (!items.length) return;
    setTacticFacets((prev) => {
      const next = new Set(prev);
      items.forEach((item) => {
        const name = item.tactic?.trim();
        if (name) next.add(name);
      });
      return Array.from(next).sort((a, b) => a.localeCompare(b));
    });
    setTechniqueFacets((prev) => {
      const next = new Set(prev);
      items.forEach((item) => {
        const name = item.techniqueName?.trim();
        if (name) next.add(name);
      });
      return Array.from(next).sort((a, b) => a.localeCompare(b));
    });
    setMitreIdFacets((prev) => {
      const next = new Set(prev);
      items.forEach((item) => {
        const id = item.mitreId?.trim();
        if (id) next.add(id);
      });
      return Array.from(next).sort((a, b) => a.localeCompare(b));
    });
  }, [items]);

  const rows = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        id: getMitreTechniqueId(item) || item.mitreId || item.techniqueName,
      })),
    [items]
  );

  const setFilter = (key: keyof Filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
    setPage(0);
  };

  const handleReset = () => {
    setFilters({});
    setSearchInput('');
    setSearch('');
    setPage(0);
  };

  const toolbarFilters: EdrToolbarFilter[] = useMemo(
    () => [
      {
        id: 'tactic',
        label: 'Tactic',
        options: tacticFacets.map((name) => ({ label: name, value: name })),
        value: filters.tactic ?? '',
        multiple: true,
        onChange: (value) => setFilter('tactic', value),
      },
      {
        id: 'technique',
        label: 'Technique',
        options: techniqueFacets.map((name) => ({ label: name, value: name })),
        value: filters.technique ?? '',
        multiple: true,
        onChange: (value) => setFilter('technique', value),
      },
      {
        id: 'severity',
        label: 'Severity',
        icon: <WarningIcon style={{ fontSize: 14 }} />,
        options: MITRE_SEVERITY_FILTER_OPTIONS,
        value: filters.severityBand ?? '',
        multiple: true,
        onChange: (value) => setFilter('severityBand', value),
      },
      {
        id: 'mitreId',
        label: 'MITRE ID',
        options: mitreIdFacets.map((id) => ({ label: id, value: id })),
        value: filters.mitreId ?? '',
        multiple: true,
        onChange: (value) => setFilter('mitreId', value),
      },
    ],
    [
      filters.tactic,
      filters.technique,
      filters.severityBand,
      filters.mitreId,
      tacticFacets,
      techniqueFacets,
      mitreIdFacets,
    ]
  );

  const columns: Column[] = useMemo(() => getMitreTechniqueListColumns('endpoint'), []);

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16, marginTop: 8, minWidth: 0 }}>
      {canSummary ? (
      <Box
        display="grid"
        style={{
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 16,
          alignItems: 'stretch',
        }}
      >
        <EdrKpiCard
          label="Active Techniques"
          value={formatEdrNumber(summary?.activeTechniques)}
          icon={<TrackChangesIcon style={{ fontSize: 18 }} />}
          iconColor={EDR_COLORS.brand500}
          accent={EDR_COLORS.brand500}
        >
          Currently observed
        </EdrKpiCard>
        <EdrKpiCard
          label="High-Risk Techniques"
          value={formatEdrNumber(summary?.highRiskTechniques)}
          icon={<WarningIcon style={{ fontSize: 18 }} />}
          iconColor={EDR_COLORS.critical}
          accent={EDR_COLORS.critical}
        >
          Require immediate investigation
        </EdrKpiCard>
        <EdrKpiCard
          label="Total Detections"
          value={formatEdrNumber(summary?.totalDetections)}
          icon={<GpsFixedIcon style={{ fontSize: 18 }} />}
          iconColor={EDR_COLORS.brand500}
        />
        <EdrKpiCard
          label="Detections by Severity"
          value={(
            <Box display="flex" flexWrap="wrap" style={{ gap: 6, fontSize: 11, fontWeight: 500 }}>
              <EdrStatusPill
                tone="critical"
                label={`${formatEdrNumber(summary?.detectionsBySeverity?.critical)} Critical`}
                dot={false}
              />
              <EdrStatusPill
                tone="high"
                label={`${formatEdrNumber(summary?.detectionsBySeverity?.high)} High`}
                dot={false}
              />
              <EdrStatusPill
                tone="medium"
                label={`${formatEdrNumber(summary?.detectionsBySeverity?.medium)} Medium`}
                dot={false}
              />
              <EdrStatusPill
                tone="success"
                label={`${formatEdrNumber(summary?.detectionsBySeverity?.low)} Low`}
                dot={false}
              />
            </Box>
          )}
          icon={<BarChartIcon style={{ fontSize: 18 }} />}
          iconColor={EDR_COLORS.critical}
        />
      </Box>
      ) : null}

      {canList ? (
      <EdrDataCard
        toolbar={(
          <EdrDataToolbar
            searchValue={searchInput}
            onSearchChange={(value) => {
              setSearchInput(value);
              debouncedSetSearch(value);
            }}
            searchPlaceholder="Search techniques…"
            filters={toolbarFilters}
            onReset={handleReset}
          />
        )}
      >
        <Table
          columns={columns}
          data={rows as unknown as TableData[]}
          loading={listLoading}
          showPagination
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={totalRecords}
          onPageChange={setPage}
          onRowsPerPageChange={(next) => {
            setRowsPerPage(next);
            setPage(0);
          }}
          onRowClick={(row) => openTechniqueDetail(row as unknown as WazuhMitreTechniqueItem)}
          TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
          MUITableProps={{ sx: { tableLayout: 'fixed', width: '100%' } }}
        />
      </EdrDataCard>
      ) : null}
    </Box>
  );
};

export default EndpointMitreTab;
